/*
* Reserve study chart - chart components
*/

import '@progress/kendo-ui';

import { Grid } from '@progress/kendo-grid-react-wrapper';
import * as FastSort from 'fast-sort';
import * as Highcharts from 'highcharts';
import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonToolbar, Tab, Tabs } from 'react-bootstrap';
import ReactHighCharts from 'react-highcharts';

import * as AppUtilities from '../common/apputilities';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import { transformComponentFundingItems } from './componentFunding';
import * as ReportComponents from './pdf/report-components';

// BOTH PORTAL AND RESERVE STUDY FORM USE THIS

/* Chart tabs */
export const ChartTabs = ({ appText, tabsId, calculations, inventoryMap, isHypothetical, beginYear, yearsCount, appTextGridColumnHeader, scenarioLabel, ...rest }) => {
    if (!appText || !tabsId || !AppUtilities.isArrayWithLength(calculations)) { return (<div />); }

    const {
        extraTabConfigs,
        formData,
        assoc,
        currentOrder,
        currentOrderChart,
        percentFunded,
        yearlySummaryData,
        handleOptimizeButton,
        handleSaveButton,
        handleRevertButton,
        onlyHypothetical,
    } = rest;

    const showButtons = (_.get(rest, 'showButtons', false)) ? true : false;
    const showSaveButton = (_.get(rest, 'showSaveButton', false)) ? true : false;
    const showOptimizeButton = (_.get(rest, 'showOptimizeButton', false)) ? true : false;
    const numFormChanges = Formatters.getInt(rest, 'numFormChanges');
    const disableButtons = (numFormChanges > 0) ? false : true;
    // console.log(`charts.tsx::ChartTabs onlyHypothetical ${onlyHypothetical} showButtons ${showButtons} disableButtons ${disableButtons} numFormChanges ${numFormChanges}`);

    const reportOptions = {
        mrcOptions: {
            showCurrent: _.get(formData, 'showCurrent', false),
            showMinimum: _.get(formData, 'showMinimum', false),
        },
        onlyHypothetical: onlyHypothetical,
    };

    let tabConfig: Array<any> = [
        {
            eventKey: 1,
            title: appText('percent-funded'),
            className: 'tab-pane-chart tab-pane-percent-funded',
            tabContent: (
                <ChartPercentFunded
                    appText={appText}
                    beginYear={beginYear}
                    calculations={calculations}
                    isHypothetical={isHypothetical}
                    yAxisTitle={appText('percent-funded')}
                    yearsCount={yearsCount}
                    reportOptions={reportOptions}
                />
            )
        },
        {
            eventKey: 2,
            title: appText('expenditures'),
            className: 'tab-pane-chart tab-pane-expenditures',
            tabContent: (
                <ChartExpenditures
                    appText={appText}
                    inventoryMap={inventoryMap}
                    isHypothetical={isHypothetical}
                    beginYear={beginYear}
                    yearsCount={yearsCount}
                    yAxisTitle={appText('expenditures')}
                />
            )
        },
        {
            eventKey: 3,
            title: appText('mrc'),
            className: 'tab-pane-chart tab-pane-mrc',
            tabContent: (
                <ChartMRC
                    appText={appText}
                    calculations={calculations}
                    isHypothetical={isHypothetical}
                    beginYear={beginYear}
                    yearsCount={yearsCount}
                    yAxisTitle={appText('mrc')}
                    reportOptions={reportOptions}
                />
            )
        },
        {
            eventKey: 4,
            title: appText('fully-funded-balance'),
            className: 'tab-pane-chart tab-pane-fully-funded-balance',
            tabContent: (
                <ChartFullyFundedBalance
                    appText={appText}
                    calculations={calculations}
                    isHypothetical={isHypothetical}
                    beginYear={beginYear}
                    yearsCount={yearsCount}
                    yAxisTitle={appText('fully-funded-balance')}
                />
            )
        },
        {
            eventKey: 5,
            title: appText('yearly-summary'),
            className: 'tab-pane-grid tab-pane-yearly-summary',
            tabContent: (
                <div>
                    <GridYearlySummary
                        appText={appText}
                        calculations={calculations}
                        isHypothetical={isHypothetical}
                        beginYear={beginYear}
                        yearsCount={yearsCount}
                        appTextGridColumnHeader={appTextGridColumnHeader}
                    />
                </div>
            )
        },
        {
            eventKey: 6,
            title: appText('component-funding'),
            className: 'tab-pane-grid tab-pane-component-funding',
            disabled: isHypothetical,
            tabContent: (
                <ComponentFunding
                    formData={formData}
                    association={assoc}
                    inventory={inventoryMap}
                    currentOrder={currentOrder}
                    currentOrderChart={currentOrderChart}
                    percentFunded={percentFunded}
                    yearlySummaryData={yearlySummaryData}
                    appText={appText}
                    calculations={calculations}
                    isHypothetical={isHypothetical}
                />
            )
        },
    ];

    if (_.size(extraTabConfigs)) {
        tabConfig = _.concat(tabConfig, extraTabConfigs);
    }

    const buttonOptions = {
        bsSize: 'sm',
        disabled: (disableButtons) ? true : false,
    };

    const scenarioSelectedText = (scenarioLabel) ? (<h3>Currently Selected Scenario: {scenarioLabel}</h3>) : ('');

    const buttons = (
        <ButtonToolbar>
            {/*(showOptimizeButton) ? (
                <Button
                    key="report-charts-tabs-buttons-optimize-scenario"
                    {...buttonOptions}
                    bsStyle="success"
                    onClick={e => { if (handleOptimizeButton) { handleOptimizeButton(e); } }}
                >
                    <i className="fa fa-check" /> {appText('optimize-scenario')}
                </Button>) : (<span />)*/}

            {(showSaveButton) ? (
                <Button
                    key="report-charts-tabs-buttons-save-scenario"
                    {...buttonOptions}
                    bsStyle="success"
                    onClick={e => { if (handleSaveButton) { handleSaveButton(e); } }}
                >
                    <i className="fa fa-save" /> {appText('save-this-scenario')}
                </Button>) : (<div />)}

            <Button
                key="report-charts-tabs-buttons-revert-scenario"
                {...buttonOptions}
                bsStyle="warning"
                onClick={e => { if (handleRevertButton) { handleRevertButton(e); } }}
            >
                <i className="fa fa-undo" /> {appText('revert-scenario')}
            </Button>
        </ButtonToolbar>
    );

    return (
        <div className="report-charts-tabs-container">
            <div className="report-charts-scenario-label">{scenarioSelectedText}</div>
            <Tabs id={tabsId}>
                {tabConfig.map((c, i) => {
                    return (
                        <Tab key={i} eventKey={c.eventKey} title={c.title} className={c.className} disabled={c.disabled}>
                            {c.tabContent}
                        </Tab>
                    );
                })}
            </Tabs>
            {(showButtons) ? (
                <div className="report-charts-tabs-buttons-container">
                    {buttons}
                </div>
            ) : (<div />)}
        </div>
    );
};

/* Chart components */
export const ChartPercentFunded = ({ appText, calculations, isHypothetical, beginYear, yearsCount, yAxisTitle, ...rest }) => {
    if (!appText || !AppUtilities.isArrayWithLength(calculations)) { return (<div />); }

    const { reportOptions } = rest;

    const onlyHypothetical = Formatters.getBool(reportOptions, 'onlyHypothetical');
    const reportMrcOptions = _.get(reportOptions, 'mrcOptions');

    const opt = getChartYAxisConfigPercent(yAxisTitle);
    // const chartData = getPCTFundedChartDataFixed(appText, calculations, isHypothetical, _.get(reportOptions, 'mrcOptions'));
    // const chartData = getPCTFundedChartData(appText, calculations, isHypothetical, _.get(reportOptions, 'mrcOptions')); // 20190425
    const chartData = getPCTFundedChartDataHypPctFunded(appText, calculations, isHypothetical, reportMrcOptions, (onlyHypothetical) ? true : false);
    const categories = getChartYearData(beginYear, yearsCount);
    const chartConfig = (onlyHypothetical) ? getHypoLineChartConfig(categories, chartData, opt) : getLineChartConfig(categories, chartData, opt);

    // console.log(`report/charts ChartPercentFunded onlyHypothetical ${onlyHypothetical}`, { reportMrcOptions, chartConfig, categories, chartData, calculations, isHypothetical, onlyHypothetical, reportOptions });

    return (
        <div className="report-chart chart-percent-funded">
            <ReactHighCharts config={chartConfig} domProps={{ height: '0' }} />
        </div>
    );
};

export const ChartExpenditures = ({ appText, inventoryMap, isHypothetical, beginYear, yearsCount, yAxisTitle, ...rest }) => {
    if (!appText || !AppUtilities.isArrayWithLength(inventoryMap) || !beginYear || !yearsCount) { return (<div />); }

    const opt = getChartYAxisConfigCurrency(appText('expenditures'));
    const chartData = getExpendituresChartData(appText, inventoryMap, beginYear, yearsCount, isHypothetical);
    const categories = getChartYearData(beginYear, yearsCount);
    const chartConfig = getColumnChartConfig(categories, chartData, opt);

    return (
        <div className="report-chart chart-expenditures">
            <ReactHighCharts config={chartConfig} domProps={{ height: '0' }} />
        </div>
    );
};

export const ChartMRC = ({ appText, calculations, isHypothetical, beginYear, yearsCount, yAxisTitle, ...rest }) => {
    if (!appText || !AppUtilities.isArrayWithLength(calculations) || !beginYear || !yearsCount) { return (<div />); }

    const { reportOptions } = rest;

    const onlyHypothetical = Formatters.getBool(reportOptions, 'onlyHypothetical');
    const reportMrcOptions = _.get(reportOptions, 'mrcOptions');

    const opt = getChartYAxisConfigCurrency(appText('mrc'));
    const chartData = getMRCChartData(appText, calculations, beginYear, yearsCount, isHypothetical, reportMrcOptions); // _.get(reportOptions, 'mrcOptions'));
    const categories = getChartYearData(beginYear, yearsCount);
    const chartConfig = getMRCColumnChartConfig(categories, chartData, opt);

    // console.log(`report/charts ChartMRC onlyHypothetical ${onlyHypothetical}`, { reportMrcOptions, chartConfig, categories, chartData, calculations, isHypothetical, onlyHypothetical, reportOptions });

    return (
        <div className="report-chart chart-mrc">
            <ReactHighCharts config={chartConfig} domProps={{ height: '0' }} />
        </div>
    );
};

export const ChartFullyFundedBalance = ({ appText, calculations, isHypothetical, beginYear, yearsCount, yAxisTitle, ...rest }) => {
    if (!appText || !AppUtilities.isArrayWithLength(calculations) || !beginYear || !yearsCount) { return (<div />); }

    const opt = getChartYAxisConfigCurrency(yAxisTitle);
    const chartData = getFullyFundedBalanceChartData(appText, calculations, beginYear, yearsCount, isHypothetical);
    const categories = getChartYearData(beginYear, yearsCount);
    const chartConfig = getColumnChartConfig(categories, chartData, opt, false, true);

    return (
        <div className="report-chart chart-fully-funded">
            <ReactHighCharts config={chartConfig} domProps={{ height: '0' }} />
        </div>
    );
};

export const GridYearlySummary = ({ appText, calculations, isHypothetical, beginYear, yearsCount, appTextGridColumnHeader, ...rest }) => {
    if (!appText || !AppUtilities.isArrayWithLength(calculations) || !beginYear || !yearsCount || !appTextGridColumnHeader) { return (<div />); }

    const gridOptions = (isHypothetical) ? GridUtilities.kendoGridOptions(
        [
            // 20180513 APPEARS THAT THIS GRID ALWAYS BINDS TO HYPOTHETICAL DATA
            // Widths must be auto and sorting disabled, otherwise grid has a horrible time rendering on less than 1600px browser
            GridUtilities.kendoGridColumnDefinition('year', appTextGridColumnHeader('year'), 60, 1),
            GridUtilities.kendoGridColumnDefinitionTextRight('h_ffb', appTextGridColumnHeader('ffb'), 110, 2, undefined, { format: '{0:c2}' }),
            GridUtilities.kendoGridColumnDefinitionTextRight('h_rb', appTextGridColumnHeader('rb'), 100, 3, undefined, { format: '{0:c2}' }),
            GridUtilities.kendoGridColumnDefinitionTextRight('h_pf', appTextGridColumnHeader('pf'), 70, 4, undefined, { format: '{0:p2}' }),
            GridUtilities.kendoGridColumnDefinitionTextRight('h_rc', appTextGridColumnHeader('rc'), 120, 5, undefined, { format: '{0:c2}' }),

            // 20190414: changed to recommended
            GridUtilities.kendoGridColumnDefinitionTextRight('h_ii', appTextGridColumnHeader('ii'), 100, 6, undefined, { format: '{0:c2}' }),
            // GridUtilities.kendoGridColumnDefinitionTextRight('r_ii', appTextGridColumnHeader('ii'), 100, 6, undefined, { format: '{0:c2}' }),

            GridUtilities.kendoGridColumnDefinitionTextRight('h_re', appTextGridColumnHeader('re'), 110, 8, undefined, { format: '{0:c2}' }),
            GridUtilities.kendoGridColumnDefinitionTextRight('h_endingBalance', appTextGridColumnHeader('erb'), 140, 9, undefined, { format: '{0:c2}' }),
        ],
        '',
        {
            sortable: null,
            excel: {},
            pdf: {},
            toolbar: [],
        },
    ) : GridUtilities.kendoGridOptions(
        [
            // 20180513 APPEARS THAT THIS GRID ALWAYS BINDS TO HYPOTHETICAL DATA
            // Widths must be auto and sorting disabled, otherwise grid has a horrible time rendering on less than 1600px browser
            GridUtilities.kendoGridColumnDefinition('year', appTextGridColumnHeader('year'), 60, 1),
            GridUtilities.kendoGridColumnDefinitionTextRight('r_ffb', appTextGridColumnHeader('ffb'), 110, 2, undefined, { format: '{0:c2}' }),
            GridUtilities.kendoGridColumnDefinitionTextRight('r_rb', appTextGridColumnHeader('rb'), 100, 3, undefined, { format: '{0:c2}' }),
            GridUtilities.kendoGridColumnDefinitionTextRight('r_pf', appTextGridColumnHeader('pf'), 70, 4, undefined, { format: '{0:p2}' }),
            GridUtilities.kendoGridColumnDefinitionTextRight('r_rc', appTextGridColumnHeader('rc'), 120, 5, undefined, { format: '{0:c2}' }),

            // 20190414: changed to recommended
            GridUtilities.kendoGridColumnDefinitionTextRight('r_ii', appTextGridColumnHeader('ii'), 100, 6, undefined, { format: '{0:c2}' }),
            // GridUtilities.kendoGridColumnDefinitionTextRight('r_ii', appTextGridColumnHeader('ii'), 100, 6, undefined, { format: '{0:c2}' }),

            GridUtilities.kendoGridColumnDefinitionTextRight('r_re', appTextGridColumnHeader('re'), 110, 8, undefined, { format: '{0:c2}' }),
            GridUtilities.kendoGridColumnDefinitionTextRight('r_erb', appTextGridColumnHeader('erb'), 140, 9, undefined, { format: '{0:c2}' }),
        ],
        '',
        {
            sortable: null,
            excel: {},
            pdf: {},
            toolbar: [],
        },
    );

    const gridData = getYearlySummaryGridData(appText, calculations, beginYear, yearsCount, isHypothetical);

    const dataSource = GridUtilities.kendoGridDataSource(
        gridData,
        {
            sort: { field: 'year', dir: 'asc' },
        }
    );

    return (
        <div className="__report-chart chart-yearly-summary">
            <Grid dataSource={dataSource} {...gridOptions} />
        </div>
    );
};

export const ComponentFunding = ({ appText, formData, association, calculations, isHypothetical, inventory, currentOrder, currentOrderChart, percentFunded, yearlySummaryData, ...rest }) => {
    if (!appText || !AppUtilities.isArrayWithLength(calculations) || !formData || !association || !currentOrder || !currentOrderChart) { return (<div />); }

    const sectionName = 'component-funding-info-tab';

    const reportOptionsGroupComponents = Formatters.getBool(currentOrderChart, 'groupComponents');
    const getTableRowKey = (id, secName, tabName = 'data') => ReportComponents.getRowKey(id, secName, tabName);

    const buildRowsNew = (data: any[], tableName: string = 'tbl') => {
        const dataItems = _.filter(data, (o) => !Formatters.getBool(o, 'placeholder'));
        let rows: any = _(dataItems)
            .map((item: any, ind: number, arr: any[]) => {
                const isFirstPage = Formatters.getBool(item, 'group.first');
                const isLastPage = Formatters.getBool(item, 'group.last');
                const isDisplayItemOnly = Formatters.getBool(item, 'wrappedItemRow');

                let res = _.stubArray();

                if (isFirstPage && reportOptionsGroupComponents) {
                    const headerRow = (isFirstPage) ? buildGroupHeaderRow(item) : undefined;
                    const hasHeaderRow = (headerRow) ? true : false;

                    if (hasHeaderRow) { res.push(headerRow); }
                }

                res.push(
                    <tr key={getTableRowKey(`${Formatters.getStr(item, '_id')}-${Formatters.getStr(item, 'wrappedItemRow_sort')}`, sectionName, `${tableName}-analysis-cfi-row-component`)}>
                        <td className="text-center">{(!isDisplayItemOnly) ? item.componentId : <span />}</td>
                        <td className="text-center">{item.componentName}</td>
                        <td className="text-center">{(!isDisplayItemOnly) ? item.usefulLife : <span />}</td>
                        <td className="text-center">{(!isDisplayItemOnly) ? <ReportComponents.PrecisionNumber value={item.remainingUsefulLife} decimals={0} /> : <span />}</td>
                        <td className="text-center">{item.quantity}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={item.avgCost} decimals={0} /> : <span />}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={item.idealBal} decimals={0} /> : <span />}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={item.cfi_curFundBalance /* currentFundBalance */} decimals={0} /> : <span />}</td>
                        <td className="text-right">{(!isDisplayItemOnly) ? <ReportComponents.Currency value={item.monthly} /> : <span />}</td>
                    </tr>
                );

                // if (isLastPage) {
                //     res.push(buildGroupSummaryRow(item));
                // }

                if (isLastPage && reportOptionsGroupComponents) {
                    const footerRow = (isLastPage) ? buildGroupSummaryRow(item) : undefined;
                    const hasFooterRow = (footerRow) ? true : false;

                    if (hasFooterRow) { res.push(footerRow); }
                }

                return res;
            })
            .value();

        // return rows;
        return _.flattenDeep(rows);
    };

    const buildFundBalance = (data: any) => {
        let sumIdealBal = 0;
        let sumCurBal = 0;

        const groups = _.filter(data, ['group.first', true]);
        _.forEach(groups, (c) => {
            const groupSubtotals = _.get(c, 'group.subTotals');
            sumIdealBal += Number(_.get(groupSubtotals, 'idealBals', 0));
            sumCurBal += Number(_.get(groupSubtotals, 'currentFundBals', 0));
        });

        let fundingPercentage = 0;
        if (sumCurBal > 0 && sumIdealBal > 0) { fundingPercentage = (sumCurBal / sumIdealBal); }

        const result = (
            <div className="pdf-page-component-funding-cfb">
                <span>{appText('page.component-funding-information.current-fund-balance')}: </span>
                <span className="pdf-page-component-funding-cfb-percent">
                    <ReportComponents.Percentage value={fundingPercentage} decimals={0} />
                </span>
            </div>
        );

        return result;
    };

    const transformedInvItems = transformComponentFundingItems(inventory, _.get(formData, 'begBalance', 0), _.get(formData, 'recommendedMRCVal', 0));
    const invSort2 = (reportOptionsGroupComponents) ?
        _.orderBy(transformedInvItems, ['subgroup', 'componentId', 'componentName_sort', 'wrappedItemRow_sort'], ['asc', 'asc', 'asc', 'asc'])
        : _.orderBy(transformedInvItems, ['componentId', 'componentName_sort', 'wrappedItemRow_sort'], ['asc', 'asc', 'asc']);

    const invSort = _.orderBy(invSort2, ['remainingUsefulLife_sort'], ['asc']);

    const grouped = _(invSort)
        .groupBy('subgroup')
        .toPairs()
        .value();

    // console.log(`inventory.len ${_.size(inventory)} invSort.len ${_.size(invSort)}`);

    const subgroups = _.flattenDeep(
        grouped.map((grp, gInd) => {
            // const sg = _.find(association.inventorySubgroups, ['groupCode', grp[0]]);
            // if (!sg || !sg.groupCode) { return null; }
            const sg = (_.find(association.inventorySubgroups, ['groupCode', Formatters.getStr(grp, '0', 'MISSING_GROUPCODE', true)])) ?
                _.find(association.inventorySubgroups, ['groupCode', Formatters.getStr(grp, '0', 'MISSING_GROUPCODE', true)]) :
                _.find(association.inventorySubgroups, ['groupCode', _.toString(_.parseInt(grp[0]))]);
            // const sg = _.find(association.inventorySubgroups, ['groupCode', grp[0]]);
            const sgMissing = (!sg || !sg.groupCode) ? true : false;

            // console.log(`grouped map gInd ${gInd} groupCode ${grp[0]} sgMissing ${sgMissing}`, { group: grp, subGroup: sg });

            if (sgMissing) { return null; }

            const groupItems = _.get(grp, '[1]', []);
            const sumAvgCostsPerUL = _.sumBy(groupItems, 'avgCost');
            const sumIdealBals = _.sumBy(groupItems, 'idealBal');
            // const sumCurrentFundBals = _.sumBy(groupItems, 'currentFundBalance');
            const sumCurrentFundBals = _.sumBy(groupItems, 'cfi_curFundBalance'); // 20191004 _.sumBy(g[1], 'currentFundBalance');
            const sumMonthlies = _.sumBy(groupItems, 'monthly');

            const groupItemsSorted = FastSort([...groupItems]).asc([
                // g => g.group.subgroupTitle,
                // g => g.group.subgroupLabel,
                // g => g.group.subgroupId,
                g => g.componentId,
                // g => g.group.gItemInd,
                // g => g.componentId,
                g => g.componentName_sort,
                g => g.remainingUsefulLife,
                g => g._id,
                g => g.wrappedItemRow_sort,
            ]);

            // const items = g[1].map((i, iInd, iArr) => {
            const items = groupItemsSorted.map((groupItem, groupItemInd, iArr) => {
                return _.merge({}, groupItem, {
                    group: {
                        first: (groupItemInd === 0) ? true : false,
                        last: (groupItemInd === _.size(iArr) - 1) ? true : false,
                        groupInd: groupItemInd,
                        gInd: gInd,
                        gItemInd: groupItemInd,
                        subgroupTitle: sg.title,
                        subgroupId: sg._id,
                        subgroupLabel: Formatters.composeInventorySubgroupLabel(sg, true), // c.label = `${c.groupCode} - ${c.title}`;
                        subTotals: {
                            avgCosts: sumAvgCostsPerUL,
                            idealBals: sumIdealBals,
                            currentFundBals: sumCurrentFundBals,
                            monthly: sumMonthlies,
                        }
                    }
                });
            });

            return items;
        })
    );

    const buildGroupHeaderRow = (groupData: any) => {
        const groupTitle = _.get(groupData, 'group.subgroupTitle', '');
        const groupSubgroupCode = Formatters.getStr(groupData, 'subgroup');

        const result = (
            <tr key={getTableRowKey(`summary-row-header-subgroup-${groupSubgroupCode}`, sectionName, 'tbl-analysis-cfi-group-header')} className="pdf-data-table-summary-row">
                <td colSpan={9} className="pdf-data-table-summary-row-colspan pdf-data-table-group-header-row text-left">{groupTitle}</td>
            </tr>
        );

        return result;
    };

    const buildGroupSummaryRow = (groupData: any) => {
        const sumAvgCost = _.get(groupData, 'group.subTotals.avgCosts', 0);
        const sumIdealBal = _.get(groupData, 'group.subTotals.idealBals', 0);
        const sumCurBal = _.get(groupData, 'group.subTotals.currentFundBals', 0);
        const sumMonthly = _.get(groupData, 'group.subTotals.monthly', 0);

        const result = (
            // <tr key={getRowKey(`summary-row-${_.get(groupData, 'id')}`)} className="pdf-data-table-summary-row">
            <tr key={getTableRowKey(`summary-row-${_.get(groupData, 'subgroup')}`, sectionName, 'tbl-analysis-cfi-group-summary')} className="pdf-data-table-summary-row">
                <td colSpan={5} className="pdf-data-table-summary-row-colspan pdf-data-table-summary-row-subtotal-title text-right">{appText('page.component-funding-information.subtotals')}:</td>
                <td className="text-right pdf-data-table-summary-row-subtotal-value"><ReportComponents.Currency value={sumAvgCost} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-subtotal-value"><ReportComponents.Currency value={sumIdealBal} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-subtotal-value"><ReportComponents.Currency value={sumCurBal} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-subtotal-value"><ReportComponents.Currency value={sumMonthly} decimals={0} /></td>
            </tr>
        );

        return result;
    };

    const buildGrandTotalRow = (data: any) => {
        let sumAvgCost = 0; // _.get(groupData, 'group.subTotals.avgCosts', 0);
        let sumIdealBal = 0; // _.get(groupData, 'group.subTotals.idealBals', 0);
        let sumCurBal = 0; // _.get(groupData, 'group.subTotals.currentFundBals', 0);
        let sumMonthly = 0; // _.get(groupData, 'group.subTotals.monthly', 0);

        const groups = _.filter(data, ['group.first', true]);
        _.forEach(groups, (c) => {
            const groupSubtotals = _.get(c, 'group.subTotals');
            sumAvgCost += Number(_.get(groupSubtotals, 'avgCosts', 0));
            sumIdealBal += Number(_.get(groupSubtotals, 'idealBals', 0));
            sumCurBal += Number(_.get(groupSubtotals, 'currentFundBals', 0));
            sumMonthly += Number(_.get(groupSubtotals, 'monthly', 0));
        });

        const result = (
            // <tr key={getRowKey('summary-row-grandtotal')} className="pdf-data-table-summary-row">
            <tr key={getTableRowKey('summary-row-grandtotal', sectionName, 'tbl-analysis-cfi-grand-total')} className="pdf-data-table-summary-row">
                <td colSpan={5} className="pdf-data-table-summary-row-colspan pdf-data-table-summary-row-grandtotal-title text-right">{appText('page.component-funding-information.grand-total')}:</td>
                <td className="text-right pdf-data-table-summary-row-grandtotal-value"><ReportComponents.Currency value={sumAvgCost} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-grandtotal-value"><ReportComponents.Currency value={sumIdealBal} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-grandtotal-value"><ReportComponents.Currency value={sumCurBal} decimals={0} /></td>
                <td className="text-right pdf-data-table-summary-row-grandtotal-value"><ReportComponents.Currency value={sumMonthly} decimals={0} /></td>
            </tr>
        );

        return result;
    };

    // CHUNK DATA/TABLES TO PAGES
    let subgroupsSorted = _.stubArray();
    if (reportOptionsGroupComponents) {
        subgroupsSorted = FastSort([...subgroups]).asc([
            // g => g.group.subgroupTitle,
            g => g.group.subgroupLabel,
            // g => g.group.subgroupId,
            // g => g.componentId,
            g => g.group.gItemInd,
            // g => g.componentId,
            // g => g.componentName_sort,
            // g => g.remainingUsefulLife,
            // g => g._id,
            // g => g.wrappedItemRow_sort,
        ]);
    } else {
        subgroupsSorted = FastSort(subgroups).by([
            // { asc: 'componentId' },
            // { asc: 'wrappedItemRow_sort' },
            { asc: 'componentId' },
            { asc: 'componentName_sort' },
            { asc: 'remainingUsefulLife' },
            { asc: '_id' },
            { asc: 'wrappedItemRow_sort' },
        ]);
    }

    // console.log(`charts-component-funding reportOptionsGroupComponents ${reportOptionsGroupComponents}`, { subgroupsSorted });

    const grandTotalRow = buildGrandTotalRow(subgroupsSorted);

    // const pages =_(subgroups)
    const pages = _(subgroupsSorted)
        // .orderBy(['group.subgroupTitle', 'group.groupInd'])
        .chunk(999999)
        .map((chunkItems, chunkInd, chunkInputArr) => {
            const keyPrefix = `chunkInd-analysis-cfi-table-${chunkInd}`;

            let rows = buildRowsNew(chunkItems, keyPrefix); // _.get(chunkItems, 'items', []), keyPrefix);

            const headRow = (
                <tr>
                    <th className="col-cfi-id">{appText('page.component-funding-information.grid-column-headers.id')}</th>
                    <th className="col-cfi-component-name">{appText('page.component-funding-information.grid-column-headers.component-name')}</th>
                    <th className="col-cfi-ul">{appText('page.component-funding-information.grid-column-headers.ul')}</th>
                    <th className="col-cfi-rul">{appText('page.component-funding-information.grid-column-headers.rul')}</th>
                    <th className="col-cfi-quantity">{appText('page.component-funding-information.grid-column-headers.quantity')}</th>
                    <th className="col-cfi-average-current-cost">{appText('page.component-funding-information.grid-column-headers.avg-current-cost')}</th>
                    <th className="col-cfi-ideal-balance">{appText('page.component-funding-information.grid-column-headers.ideal-balance')}</th>
                    <th className="col-cfi-current-fund-balance">{appText('page.component-funding-information.grid-column-headers.current-fund-balance')}</th>
                    <th className="col-cfi-monthly">{appText('page.component-funding-information.grid-column-headers.monthly')}</th>
                </tr>
            );

            let fundBalance = (<div />);
            if ((chunkInd + 1) === _.size(chunkInputArr)) {
                rows.push(grandTotalRow);
                // fundBalance = buildFundBalance(subgroups);
                fundBalance = buildFundBalance(subgroupsSorted);
            }

            // <div key={getRowKey(keyPrefix)} className="pdf-data-table-component-funding-chunkpage">

            const chunkPage = (
                <div key={getTableRowKey(`cfi-analysis-page-keyPrefix-${keyPrefix}`, sectionName, 'tbl-analysis-cgi-chunk-page')} className="pdf-data-table-component-funding-chunkpage">
                    <ReportComponents.ReportTable
                        classNameTable="pdf-data-table-component-funding"
                        pageIndex={chunkInd}
                        tbodyRows={rows}
                        theadRow={headRow}
                    />
                </div>
            );

            return chunkPage;
        })
        .value();

    return (
        <div className="pdf-data-table-component-funding-pages-wrapper">
            {pages}
        </div>
    );
};

/* Chart data loading */
export const getPCTFundedChartDataHypPctFunded = (appText: (e: any) => string, calculations: Array<any>, isHypothetical: boolean, mrcOptions?: any, onlyHypothetical?: boolean) => {
    // Workaround for chart values not matching table data
    let chartData: Array<any> = [];

    if (!appText || !AppUtilities.isArrayWithLength(calculations)) { return chartData; }

    const showCurrent = mrcOptions ? _.get(mrcOptions, 'showCurrent', false) : false;
    const showMinimum = mrcOptions ? _.get(mrcOptions, 'showMinimum', false) : false;

    let a = calculations;

    if (onlyHypothetical) {
        return [{
            name: appText('hypothetical-pct-funded'),
            data: a.map((item, idx) => (item.hypoPctFunded === Infinity) ? null : item.hypoPctFunded),
            visible: true,
        }];
    }

    if (isHypothetical) {
        return [{
            name: appText('hypothetical-pct-funded'),
            data: a.map((item, idx) => (item.hypoPctFunded === Infinity) ? null : item.hypoPctFunded),
            visible: true,
        }
        ];
    } else {
        return [{
            name: appText('recommended-pct-funded'),
            // data: a.map((item, idx) => item.recommendedPctFunded),
            visible: true,
            data: a.map((item, idx) => (item.recommendedPctFunded === Infinity) ? null : item.recommendedPctFunded), // 20200303 was:
            // data: a.map((item, idx) => (item.hypoPctFunded === Infinity) ? null : item.hypoPctFunded),
        }, {
            name: appText('current-pct-funded'),
            data: a.map((item, idx) => item.currentPctFunded),
            visible: showCurrent,
        }, {
            name: appText('minimum-pct-funded'),
            data: a.map((item, idx) => item.minimumPctFunded),
            visible: showMinimum,
        }
        ];
    }
};

export const getPCTFundedChartData = (appText: (e: any) => string, calculations: Array<any>, isHypothetical: boolean, mrcOptions?: any) => {
    // DEPRECATED - recommended % funded line series data wrong, should be hypopctfunded?
    let chartData: Array<any> = [];

    if (!appText || !AppUtilities.isArrayWithLength(calculations)) { return chartData; }

    const showCurrent = mrcOptions ? _.get(mrcOptions, 'showCurrent', false) : false;
    const showMinimum = mrcOptions ? _.get(mrcOptions, 'showMinimum', false) : false;

    let a = calculations;
    if (isHypothetical) {
        return [{
            name: appText('hypothetical-pct-funded'),
            data: a.map((item, idx) => (item.hypoPctFunded === Infinity) ? null : item.hypoPctFunded),
            visible: true,
        }
        ];
    } else {
        return [{
            name: appText('recommended-pct-funded'),
            data: a.map((item, idx) => item.recommendedPctFunded),
            visible: true,
        }, {
            name: appText('current-pct-funded'),
            data: a.map((item, idx) => item.currentPctFunded),
            visible: showCurrent,
        }, {
            name: appText('minimum-pct-funded'),
            data: a.map((item, idx) => item.minimumPctFunded),
            visible: showMinimum,
        }
        ];
    }
};

export const getPCTFundedChartDataFixed = (appText: (e: any) => string, calculations: Array<any>, isHypothetical: boolean, mrcOptions?: any) => {
    let chartData: Array<any> = [];

    if (!appText || !AppUtilities.isArrayWithLength(calculations)) { return chartData; }

    const showCurrent = mrcOptions ? _.get(mrcOptions, 'showCurrent', false) : false;
    const showMinimum = mrcOptions ? _.get(mrcOptions, 'showMinimum', false) : false;

    let a = calculations;
    if (isHypothetical) {
        return [{
            name: appText('hypothetical-pct-funded'),
            data: a.map((item, idx) => (item.hypoPctFunded === Infinity) ? null : item.hypoPctFunded),
            visible: true,
        }
        ];
    } else {
        return [{
            name: appText('recommended-pct-funded'),
            data: a.map((item, idx) => item.recommendedPctFunded),
            visible: true,
        }, {
            name: appText('current-pct-funded'),
            data: a.map((item, idx) => item.currentPctFunded),
            visible: showCurrent,
        }, {
            name: appText('minimum-pct-funded'),
            data: a.map((item, idx) => item.minimumPctFunded),
            visible: showMinimum,
        }
        ];
    }
};

export const getPCTFundedChartDataFixedWORKING0409 = (appText: (e: any) => string, calculations: Array<any>, isHypothetical: boolean, mrcOptions?: any) => {
    let chartData: Array<any> = [];

    if (!appText || !AppUtilities.isArrayWithLength(calculations)) { return chartData; }

    const showCurrent = mrcOptions ? _.get(mrcOptions, 'showCurrent', false) : false;
    const showMinimum = mrcOptions ? _.get(mrcOptions, 'showMinimum', false) : false;

    let a = calculations;
    if (isHypothetical) {
        return [{
            name: appText('recommended-pct-funded'),
            data: a.map((item, idx) => item.recommendedPctFunded),
            visible: false
        }, {
            name: appText('current-pct-funded'),
            data: a.map((item, idx) => item.currentPctFunded),
            visible: false
        }, {
            name: appText('minimum-pct-funded'),
            data: a.map((item, idx) => item.minimumPctFunded),
            visible: false
        }, {
            name: appText('hypothetical-pct-funded'),
            data: a.map((item, idx) => (item.hypoPctFunded === Infinity) ? null : item.hypoPctFunded),
            visible: true,
        }
        ];
    } else {
        return [{
            name: appText('recommended-pct-funded'),
            data: a.map((item, idx) => item.recommendedPctFunded), // item.hypoPctFunded),
            visible: true,
        }, {
            name: appText('current-pct-funded'),
            data: a.map((item, idx) => item.currentPctFunded),
            visible: showCurrent,
        }, {
            name: appText('minimum-pct-funded'),
            data: a.map((item, idx) => item.minimumPctFunded),
            visible: showMinimum,
        }
        ];
    }
};

export const getChartYearData = (beginYear: number, yearsCount: number) => {
    let years: Array<any> = [];

    for (let y: number = 0; y < yearsCount; y++) {
        const year = Number(beginYear) + Number(y);
        years.push(year.toString());
    }

    return years;
};

export const getChartCategoriesYearDates = (beginYear: number, yearsCount: number) => {
    let years: Array<any> = [];

    for (let y: number = 0; y < yearsCount; y++) {
        const year = Number(beginYear) + Number(y);
        years.push(new Date(year, 0, 1, 0, 0, 0, 0));
    }

    return years;
};

export const getExpendituresChartData = (appText: (e: any) => string, inventoryMap: Array<any>, beginYear: number, yearsCount: number, isHypothetical: boolean) => {
    let chartData: Array<number> = [];

    if (!appText || !AppUtilities.isArrayWithLength(inventoryMap) || !beginYear || !yearsCount) { return chartData; }

    for (let y = 0; y < yearsCount; y++) {
        chartData.push(0);

        inventoryMap.forEach((inventory) => {
            const expense = Number(inventory.expenditureMap[y].expense);
            chartData[y] += Number(expense);
        });

        chartData[y] = Math.round(chartData[y] * 100) / 100;
    }

    let hypoChartData: Array<number> = [];
    for (let x = 0; x < yearsCount; x++) {
        hypoChartData.push(0);

        inventoryMap.forEach((inventory) => {
            hypoChartData[x] = Number(hypoChartData[x]) + Number(inventory.expenditureMap[x].hypoExpense);
        });

        hypoChartData[x] = Math.round(hypoChartData[x] * 100) / 100;
    }

    if (isHypothetical) {
        return [// {
            //     name: appText('expenditures-by-year'),
            //     data: chartData,
            //     visible: true
            // },
            {
                name: appText('hypothetical-expenditures-by-year'),
                data: hypoChartData
            }];
    } else {
        return [{
            name: appText('expenditures-by-year'),
            data: chartData
        }];
    }
};

export const getMRCChartData = (
    appText: (e: any) => string,
    calculations: Array<any>,
    beginYear: number,
    yearsCount: number,
    isHypothetical: boolean,
    mrcOptions?: any) => {

    let chartData: Array<number> = [];

    if (!appText || !AppUtilities.isArrayWithLength(calculations) || !beginYear || !yearsCount) { return chartData; }

    let a = calculations;
    const showCurrent = mrcOptions ? _.get(mrcOptions, 'showCurrent', false) : false;
    const showMinimum = mrcOptions ? _.get(mrcOptions, 'showMinimum', false) : false;

    if (isHypothetical) {
        return [{
            name: appText('hypothetical-mrc'),
            data: a.map((item) => item.hypoMRC), // Income)
        }
        ];
    } else {
        return [
            {
                name: appText('recommended-mrc'),
                data: a.map((item) => item.recommendedMRC), // Income),
                visible: true,
            },
            {
                name: appText('current-mrc'),
                data: a.map((item) => item.currentMRC), // Income),
                visible: showCurrent,
            },
            {
                name: appText('minimum-mrc'),
                data: a.map((item) => item.minimumMRC), // Income),
                visible: showMinimum,
            },
        ];
    }
};

export const getReserveContributionsChartData = (appText: (e: any) => string, calculations: Array<any>, beginYear: number, yearsCount: number, isHypothetical: boolean) => {
    let chartData: Array<number> = [];

    if (!appText || !AppUtilities.isArrayWithLength(calculations) || !beginYear || !yearsCount) { return chartData; }

    let a = calculations;

    if (isHypothetical) {
        return [{
            name: appText('hypothetical-mrc'),
            data: a.map((item) => item.hypoMRC)
        }
        ];
    } else {
        return [
            {
                name: appText('recommended-mrc'),
                data: a.map((item) => item.recommendedMRC),
            },
            {
                name: appText('current-mrc'),
                data: a.map((item) => item.currentMRC),
            },
            {
                name: appText('minimum-mrc'),
                data: a.map((item) => item.minimumMRC),
            },
        ];
    }
};

export const getSignificantComponentsChartData = (appText: (e: any) => string, components: Array<any>, colors?: Array<any>) => {
    if (!appText || !AppUtilities.isArrayWithLength(components)) { return []; }

    let chartColors = (colors && _.size(colors)) ? colors : [highColorDef.bgSystem, highColorDef.bgPrimary, highColorDef.bgSuccess, highColorDef.bgWarning];
    return [
        {
            name: 'Percentage',
            colorByPoint: true,
            data: _.orderBy(components, 'percent', ['desc']).map((item, ind) => {
                const perc = Math.round(item.percent * 100);
                return {
                    y: perc,
                    name: `${perc}% ${item.componentName}`,
                    sliced: (ind === 0) ? true : false,
                    color: _.get(chartColors, `[${ind}]`, chartColors[0]),
                    componentName: item.componentName,
                    exploded: false,
                };
            }),
            visible: true
        },
    ];
};

export const getFullyFundedBalanceChartData = (appText: (e: any) => string, calculations: Array<any>, beginYear: number, yearsCount: number, isHypothetical: boolean) => {
    let chartData: Array<number> = [];

    if (!appText || !AppUtilities.isArrayWithLength(calculations) || !beginYear || !yearsCount) { return chartData; }

    let a = calculations;

    if (isHypothetical) {
        return [
            // {
            //     name: appText('fully-funded-balance'),
            //     data: a.map((item) => item.currentFFB),
            //     visible: false
            // },
            {
                name: appText('hypothetical-fully-funded-balance'),
                data: a.map((item) => item.hypoFFB)
            }
        ];
    } else {
        return [
            {
                name: appText('fully-funded-balance'),
                data: a.map((item) => item.currentFFB),
            },
        ];
    }
};

export const getYearlySummaryGridData = (appText: (e: any) => string, calculations: Array<any>, beginYear: number, yearsCount: number, isHypothetical: boolean) => {
    if (!appText || !AppUtilities.isArrayWithLength(calculations) || !beginYear || !yearsCount) { return []; }

    const result = calculations.map((item) => {

        return _.merge(
            {
                calculationItem: _.cloneDeep(item),
                year: item.year,
            },
            {
                // CURRENT VALUES
                erb: item.currentBalance,
                ffb: item.currentFFB,
                effb: item.endingFFB,
                ii: item.currentInterestIncome,
                pf: (item.currentPctFunded !== 0) ? item.currentPctFunded / 100 : 0,
                rb: item.currentStartingBalance,
                rc: item.currentMRCIncome, // 20180523 changed from: item.currentMRC * 12,
                rcTotalWithSA: item.currentMRCIncome,
                re: item.currentExpenditures,
            },
            {
                // HYPOTHETICAL VALUES
                h_erb: item.hypoBalance,
                h_ffb: item.hypoFFB,
                h_effb: item.endingHypoFFB,
                h_ii: item.hypoInterestIncome,
                h_sa: item.hypoSpecialAssessments,
                h_pf: (item.hypoPctFunded !== 0) ? item.hypoPctFunded / 100 : 0,
                h_rb: item.hypoStartingBalance,
                h_rc: item.hypoMRCIncome, // 20180523 changed from: item.hypoMRC * 12,
                h_rcTotalWithSA: item.hypoMRCIncome + item.hypoSpecialAssessments,
                h_rc_incorrect: item.hypoMRC * 12,
                h_re: item.hypoExpenditures,
                h_fa: item.hypoStartingBalance + item.hypoMRCIncome + item.hypoInterestIncome,
                h_fa_incorrect: item.hypoStartingBalance + (item.hypoMRC * 12) + item.hypoInterestIncome,
                h_netEndingBalance: (item.hypoStartingBalance + item.hypoMRCIncome + item.hypoInterestIncome) - item.hypoExpenditures,
                h_netEndingBalance_incorrect: (item.hypoStartingBalance + (item.hypoMRC * 12) + item.hypoInterestIncome) - item.hypoExpenditures,
                h_reserveIncome: item.hypoReserveIncome,
                h_endingBalance: item.hypoEndingBalance,
            },
            {
                // RECOMMENDED VALUES // 
                r_rc: item.recommendedMRCIncome,
                r_erb: item.recommendedBalance,
                r_bal: item.recommendedBalance,
                r_ffb: item.currentFFB,
                r_effb: item.endingFFB,
                r_rb: item.recommendedStartingBalance,
                r_ii: item.recommendedInterestIncome,
                r_re: item.currentExpenditures,
                r_mrc: item.recommendedMRC,
                r_mrcIncome: item.recommendedMRCIncome,
                r_mrcIncomeTotalWithSA: item.recommendedMRCIncome + item.recommendedSpecialAssessments,
                r_sa: item.recommendedSpecialAssessments,
                // r_pf: item.recommendedPctFunded, 09262019 working
                r_pf: (item.recommendedPctFunded !== 0) ? item.recommendedPctFunded / 100 : 0,
                r_sb: item.recommendedStartingBalance,
            },
            {
                // MINIMUM VALUES
                m_erb: item.minimumBalance,
                m_ffb: item.currentFFB, // item.hypoFFB,
                m_effb: item.endingFFB,
                m_ii: item.minimumInterestIncome,
                m_pf: (item.minimumPctFunded !== 0) ? item.minimumPctFunded / 100 : 0,
                m_rb: item.minimumStartingBalance,
                m_rc: item.minimumMRCIncome,
                m_re: item.currentExpenditures, // item.hypoExpenditures, // TODO ???
                m_fa: item.minimumStartingBalance + item.minimumMRCIncome + item.minimumInterestIncome,
            },
            {
                // CA_DISCLOSURE VALUES // 
                caDisc_rc: item.caDiscMRCIncome,
                caDisc_erb: item.caDiscBalance,
                caDisc_bal: item.caDiscBalance,
                caDisc_ffb: item.currentFFB,
                caDisc_effb: item.endingFFB,
                caDisc_rb: item.caDiscStartingBalance,
                caDisc_ii: item.caDiscInterestIncome,
                caDisc_re: item.currentExpenditures,
                caDisc_mrc: item.caDiscMRC,
                caDisc_mrcIncome: item.caDiscMRCIncome,
                caDisc_mrcIncomeTotalWithSA: item.caDiscMRCIncome + item.caDiscSpecialAssessments,
                caDisc_sa: item.caDiscSpecialAssessments,
                caDisc_pf: (item.caDiscPctFunded !== 0) ? item.caDiscPctFunded / 100 : 0,
                caDisc_sb: item.caDiscStartingBalance,
            },
        );
    });

    // console.log('getYearlySummaryGridData', {beginYear, calculations, result});

    return result;
};

/* Chart configuration */
export const highColorDef = {
    'bgAlert': '#967adc',
    'bgAlertD': '#815fd5',
    'bgAlertDr': '#6c44ce',
    'bgAlertL': '#ac92ec',
    'bgAlertLr': '#c0b0ea',
    'bgBlack': '#283946',
    'bgBlackD': '#1c2730',
    'bgBlackDr': '#0f161b',
    'bgBlackL': '#2e4251',
    'bgBlackLr': '#354a5b',
    'bgDanger': '#e9573f',
    'bgDangerD': '#e63c21',
    'bgDangerDr': '#cd3117',
    'bgDangerL': '#fc6e51',
    'bgDangerLr': '#f08c7c',
    'bgDark': '#3b3f4f',
    'bgDarkD': '#2c2f3c',
    'bgDarkDr': '#1e2028',
    'bgDarkL': '#424759',
    'bgDarkLr': '#51566c',
    'bgInfo': '#3bafda',
    'bgInfoD': '#27a0cc',
    'bgInfoDr': '#2189b0',
    'bgInfoL': '#4fc1e9',
    'bgInfoLr': '#74c6e5',
    'bgLight': '#f3f6f7',
    'bgLightD': '#e9eef0',
    'bgLightDr': '#dfe6e9',
    'bgLightL': '#fdfefe',
    'bgLightLr': '#ffffff',
    'bgPrimary': '#4a89dc',
    'bgPrimaryD': '#2e76d6',
    'bgPrimaryDr': '#2567bd',
    'bgPrimaryL': '#5d9cec',
    'bgPrimaryLr': '#83aee7',
    'bgSuccess': '#70ca63',
    'bgSuccessD': '#58c249',
    'bgSuccessDr': '#49ae3b',
    'bgSuccessL': '#87d37c',
    'bgSuccessLr': '#9edc95',
    'bgSystem': '#37bc9b',
    'bgSystemD': '#2fa285',
    'bgSystemDr': '#288770',
    'bgSystemL': '#48cfad',
    'bgSystemLr': '#65d2b7',
    'bgWarning': '#f6bb42',
    'bgWarningD': '#f4af22',
    'bgWarningDr': '#d9950a',
    'bgWarningL': '#ffce54',
    'bgWarningLr': '#f9d283',
    'recommended': '#0000ff', // complex solutions report charting colors
    'current': '#dc143c', // complex solutions report charting colors
    'minimum': '#32cd32', // complex solutions report charting colors
    'hypothetical': '#4a89dc', // complex solutions report charting colors (blue)
    'sigComp1': '#008102', // complex solutions report charting colors (significant components)
    'sigComp2': '#0000fe', // complex solutions report charting colors (significant components)
    'sigComp3': '#81007f', // complex solutions report charting colors (significant components)
    'sigComp4': '#01ff02', // complex solutions report charting colors (significant components)
    'sigComp5': '#ff00ff', // complex solutions report charting colors (significant components)
    'currentYRE': '#008102', // complex solutions report charting colors (yearly reserve expenditures, current expenditures)
    'recommendedMRC': '#0808ff',
    'currentMRC': '#008102',
    'minimumMRC': '#2cce2f',
};

export const getBrandedChartColors = () => {
    const result = [highColorDef.recommended, highColorDef.current, highColorDef.minimum, highColorDef.hypothetical];

    return result;
};

export const getBrandedReserveContributionChartColors = () => {
    const result = [highColorDef.recommendedMRC, highColorDef.currentMRC, highColorDef.minimumMRC, highColorDef.hypothetical];

    return result;
};

export const getBrandedPieChartColors = () => {
    const result = [highColorDef.sigComp1, highColorDef.sigComp2, highColorDef.sigComp3, highColorDef.sigComp4, highColorDef.sigComp5];

    return result;
};

export const getBrandedBarChartColors = () => {
    const result = [highColorDef.currentYRE, highColorDef.recommended, highColorDef.minimum, highColorDef.hypothetical];

    return result;
};

export const getBrandedChartColorConfig = () => {
    const result = {
        colors: getBrandedChartColors(),
    };

    return result;
};

export const getChartConfig = (type: string, colors: Array<any>, categories: Array<any> = [], chartData: Array<any>, chartOptions: any = {}) => {
    if (!type || !AppUtilities.isArrayWithLength(colors) || (type !== 'pie' && !AppUtilities.isArrayWithLength(categories)) || !AppUtilities.isArrayWithLength(chartData)) { return {}; }

    return _.merge(
        {
            credits: false,
            colors: colors,
            chart: {
                className: '',
                type: type,
                zoomType: 'x',
                panning: true,
                panKey: 'shift',
                marginTop: 45,
                marginRight: 1,
            },
            tooltip: {
                useHTML: true,
            },
            title: {
                text: null,
            },
            xAxis: {
                gridLineColor: '#EEE',
                lineColor: '#EEE',
                tickColor: '#EEE',
                categories: categories,
            },
            yAxis: {
                min: 0,
                // tickInterval: 5,
                gridLineColor: '#999999',
                title: {
                    text: null
                },
                showFirstLabel: false,
            },
            plotOptions: {
                spline: {
                    lineWidth: 3
                },
                area: {
                    fillOpacity: 0.2
                },
                pie: {
                    allowPointSelect: true,
                    cursor: 'pointer',
                    depth: 55,
                    dataLabels: {
                        format: '<span style="font-size: 14px; font-weight: 500;">{point.percentage:.0f}%</span>',
                        distance: 10,
                        enabled: true
                    },
                    showInLegend: true,
                }
            },
            legend: {
                enabled: true,
                floating: false,
                align: 'right',
                verticalAlign: 'top',
                x: -15,
                y: -15,
            },
            series: chartData,
        },
        chartOptions);
};

export const getChartYAxisConfig = (title: string = '', labelFormatter?: (t: any) => string, tooltipPrefix?: string, tooltipSuffix?: string) => {
    let config = {
        tooltip: {
            valueDecimals: 2,
        },
        yAxis: {
            labels: {},
            title: {
                text: null,
            }
        }
    };

    if (labelFormatter) { config = _.set(config, 'yAxis.labels.formatter', labelFormatter); }
    if (title) { config = _.set(config, 'yAxis.title.text', title); }
    if (tooltipPrefix) { config = _.set(config, 'tooltip.valuePrefix', tooltipPrefix); }
    if (tooltipSuffix) { config = _.set(config, 'tooltip.valueSuffix', tooltipSuffix); }

    return config;
};

export const getChartYAxisConfigCurrency = (title: string = '', symbol: string = '$') => {
    return getChartYAxisConfig(
        title,
        function (this: any) {
            const val = this.axis.defaultLabelFormatter.call(this);
            return `${symbol}${val}`;
        },
        symbol,
        ''
    );
};

export const getChartYAxisConfigPercent = (title: string = '', symbol: string = '%') => {
    return getChartYAxisConfig(
        title,
        function (this: any) {
            const val = this.axis.defaultLabelFormatter.call(this);
            return `${val}${symbol}`;
        },
        '',
        symbol
    );
};

export const getLineChartConfig = (categories: Array<any>, chartData: Array<any>, chartOptions: any = {}, enable3d: boolean = false) => {
    // let highColors = [highColorDef.bgSystem, highColorDef.bgSuccess, highColorDef.bgWarning, highColorDef.bgPrimary];
    // these colors were changed Aug 18th, 2021 to match the ones in the report pdf. 
    let highColors = [highColorDef.recommended, highColorDef.current, highColorDef.minimum, highColorDef.bgPrimary];

    const options3d = {
        chart: {
            options3d: {
                enabled: (enable3d) ? true : false,
                alpha: 0,
                beta: 0
            }
        }
    };

    return getChartConfig('spline', highColors, categories, chartData, _.merge({}, chartOptions, options3d));
};

export const getHypoLineChartConfig = (categories: Array<any>, chartData: Array<any>, chartOptions: any = {}, enable3d: boolean = false) => {
    let highColors = [highColorDef.hypothetical, highColorDef.hypothetical, highColorDef.hypothetical, highColorDef.hypothetical];

    const options3d = {
        chart: {
            options3d: {
                enabled: (enable3d) ? true : false,
                alpha: 0,
                beta: 0
            }
        }
    };

    return getChartConfig('spline', highColors, categories, chartData, _.merge({}, chartOptions, options3d));
};

export const getColumnChartConfig = (categories: Array<any>, chartData: Array<any>, chartOptions: any = {}, enable3d: boolean = false, only2Series: boolean = false) => {
    let highColors2 = [highColorDef.bgSystem, highColorDef.bgSuccess, highColorDef.bgWarning, highColorDef.bgPrimary];
    // 20200302 was: let highColors2 = [highColorDef.bgSystem, highColorDef.bgPrimary, highColorDef.bgSuccess, highColorDef.bgWarning];

    if (only2Series) {
        highColors2 = [highColorDef.bgSystem, highColorDef.bgPrimary];
    }

    // console.log(`charts.tsx::getColumnChartConfig categories.len ${_.size(categories)} only2Series ${only2Series}`, { categories, chartOptions, chartData });

    const options3d = {
        chart: {
            options3d: {
                enabled: (enable3d) ? true : false,
                alpha: 0,
                beta: 0,
                viewDistance: 15,
                depth: 65,
            }
        }
    };

    return getChartConfig('column', highColors2, categories, chartData, _.merge({}, chartOptions, options3d));
};

export const getMRCColumnChartConfig = (categories: Array<any>, chartData: Array<any>, chartOptions: any = {}, enable3d: boolean = false, only2Series: boolean = false) => {
    let highColors2 = [highColorDef.recommended, highColorDef.current, highColorDef.minimum, highColorDef.bgPrimary];

    if (only2Series) {
        highColors2 = [highColorDef.bgSystem, highColorDef.bgPrimary];
    }

    const options3d = {
        chart: {
            options3d: {
                enabled: (enable3d) ? true : false,
                alpha: 0,
                beta: 0,
                viewDistance: 15,
                depth: 65,
            }
        }
    };

    return getChartConfig('column', highColors2, categories, chartData, _.merge({}, chartOptions, options3d));
};

export const getPieChartConfig = (chartData: Array<any>, chartOptions: any = {}, enable3d: boolean = true) => {
    let highColors2 = [highColorDef.bgSystem, highColorDef.bgPrimary, highColorDef.bgSuccess, highColorDef.bgWarning];

    const options3d = {
        chart: {
            marginTop: 0,
            options3d: {
                enabled: (enable3d) ? true : false,
                alpha: 40,
                beta: 0
            }
        }
    };

    return getChartConfig('pie', highColors2, [], chartData, _.merge({}, chartOptions, options3d));
};

export const setHighchartOptions = () => {
    return Highcharts.setOptions({
        lang: {
            thousandsSep: ',',
            numericSymbols: null,
        }
    });
};



// WEBPACK FOOTER //
// ./src/report/charts.tsx